import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import Schedule from '../components/scheduling/Schedule';
import unifiedApi from '../utils/unifiedApi';

const SchedulePage = () => {
  const { leagueSlug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadScheduleData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch complete schedule data including league info and games
        const data = await unifiedApi.getLeagueSchedule(leagueSlug);
        
        if (!data) {
          setError('League not found or not public');
          return;
        }

        setScheduleData(data);
      } catch (err) {
        setError('Failed to load league schedule');
        console.error('Error loading league schedule:', err);
      } finally {
        setLoading(false);
      }
    };

    if (leagueSlug) {
      loadScheduleData();
    }
  }, [leagueSlug]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!scheduleData) {
    return (
      <Box mt={4}>
        <Alert severity="info">No league information available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Page Header */}
      <Box className="page-header" sx={{ px: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ScheduleIcon color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }} />
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            className="page-title"
            sx={{ wordBreak: 'break-word' }}
          >
            {scheduleData.leagueName} - Schedule
          </Typography>
        </Box>
        <Typography 
          variant={isMobile ? "body2" : "body1"} 
          color="text.secondary"
          sx={{ mt: { xs: 0.5, sm: 1 } }}
        >
          Complete league schedule and game results
        </Typography>
      </Box>

      {/* Schedule Table */}
      <Paper sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Schedule 
          leagueId={scheduleData.leagueId}
          scheduleData={scheduleData}
          showFilter={true}
        />
      </Paper>
    </Box>
  );
};

export default SchedulePage;